import { useCallback, useEffect, useRef, useState } from "react";
import { IFavoriteOriginWithMonths } from "../../../types/IFavoriteOrigins";

interface UseAddOriginTourParams {
  selectedProduct: string;
  kochOrigins: IFavoriteOriginWithMonths[];
  competitorOrigins: IFavoriteOriginWithMonths[];
}

export const useAddOriginTour = ({
  selectedProduct,
  kochOrigins,
  competitorOrigins,
}: UseAddOriginTourParams) => {
  const addOriginButtonRef = useRef<HTMLButtonElement | null>(null);
  // Not persisted: dismissing should only hide the tour for the current visit, not forever.
  const [isAddOriginTourOpen, setIsAddOriginTourOpen] = useState(true);
  const [addOriginTourSpotlightRect, setAddOriginTourSpotlightRect] =
    useState<DOMRect | null>(null);

  const hasNoFavoriteOrigins =
    kochOrigins.length === 0 && competitorOrigins.length === 0;
  const isAddOriginTourVisible = isAddOriginTourOpen && hasNoFavoriteOrigins;

  const dismissAddOriginTour = useCallback(() => {
    setIsAddOriginTourOpen(false);
  }, []);

  useEffect(() => {
    setIsAddOriginTourOpen(true);
  }, [selectedProduct]);

  useEffect(() => {
    if (!isAddOriginTourVisible) {
      return undefined;
    }

    let frameId: number;
    const updateSpotlightRect = () => {
      const buttonEl = addOriginButtonRef.current;
      if (buttonEl) {
        const nextRect = buttonEl.getBoundingClientRect();
        setAddOriginTourSpotlightRect((prev) => {
          if (
            prev &&
            prev.top === nextRect.top &&
            prev.left === nextRect.left &&
            prev.width === nextRect.width &&
            prev.height === nextRect.height
          ) {
            return prev;
          }
          return nextRect;
        });
      }
      frameId = requestAnimationFrame(updateSpotlightRect);
    };

    frameId = requestAnimationFrame(updateSpotlightRect);
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [isAddOriginTourVisible]);

  useEffect(() => {
    if (!isAddOriginTourVisible) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismissAddOriginTour();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAddOriginTourVisible, dismissAddOriginTour]);

  return {
    addOriginButtonRef,
    isAddOriginTourVisible,
    addOriginTourSpotlightRect,
    dismissAddOriginTour,
  };
};
